'use client';

import {
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { styled, useThemeProps } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Context – exported for use by ColumnResizer
// ---------------------------------------------------------------------------

interface ResizableTableContextValue {
    getColumnWidth: (columnId: string) => number | undefined;
    onResize: (columnId: string, delta: number) => void;
}

const ResizableTableContext = createContext<ResizableTableContextValue | null>(null);

/**
 * Hook for ColumnResizer (or other children) to access the resize context.
 * Returns `null` when used outside a ResizableTableContainer so the resizer
 * can still work in a standalone / uncontrolled fashion.
 */
const useResizableTable = () => useContext(ResizableTableContext);

// ---------------------------------------------------------------------------
// Styled wrapper
// ---------------------------------------------------------------------------

const ResizableTableContainerRoot = styled('div', {
    name: 'MuiResizableTableContainer',
    slot: 'root',
})({
    overflowX: 'auto',
    position: 'relative',
    '& table': {
        tableLayout: 'fixed',
    },
    '& th': {
        position: 'relative',
    },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MIN_COLUMN_WIDTH = 50;

interface ResizableTableContainerProps {
    children: ReactNode;
    className?: string;
    /** Initial column widths keyed by column id. */
    defaultColumnWidths?: Record<string, number>;
    /** Callback fired after a column is resized. */
    onColumnResize?: (columnId: string, width: number) => void;
}

const ResizableTableContainer = forwardRef<HTMLDivElement, ResizableTableContainerProps>(
    function ResizableTableContainer(inProps, ref) {
        const props = useThemeProps({ props: inProps, name: 'MuiResizableTableContainer' });
        const { children, className, defaultColumnWidths, onColumnResize, ...other } = props;

        const [columnWidths, setColumnWidths] = useState(defaultColumnWidths ?? {});

        const getColumnWidth = useCallback(
            (columnId: string) => columnWidths[columnId],
            [columnWidths]
        );

        const onResize = useCallback(
            (columnId: string, delta: number) => {
                setColumnWidths((prev) => {
                    const current = prev[columnId] ?? MIN_COLUMN_WIDTH;
                    const next = Math.max(MIN_COLUMN_WIDTH, current + delta);
                    if (next === current) return prev;
                    onColumnResize?.(columnId, next);
                    return { ...prev, [columnId]: next };
                });
            },
            [onColumnResize]
        );

        const ctx = useMemo<ResizableTableContextValue>(
            () => ({ getColumnWidth, onResize }),
            [getColumnWidth, onResize]
        );

        return (
            <ResizableTableContext.Provider value={ctx}>
                <ResizableTableContainerRoot ref={ref} className={className} {...other}>
                    {children}
                </ResizableTableContainerRoot>
            </ResizableTableContext.Provider>
        );
    }
);

export { ResizableTableContainer, useResizableTable };
export type { ResizableTableContainerProps };
